import React, { useState, useEffect, useRef } from 'react';

import { Typography, Grid } from '@material-ui/core';
import { pick } from 'lodash';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useHistory } from 'react-router-dom';

import { ButtonComponent } from 'components';
import { VALIDATORS_MAP } from 'components/FormComponents/const';
import { useAuthActions, useAuthState } from 'contexts/AuthContext';
import { useServerErrorActions } from 'contexts/ServerErrorContext';
import { useResetClientGlobalContexts, useSubmittingState } from 'hooks';
import { authModel, tfaModel } from 'models';
import { ROUTES } from 'routes/constants';
import { useMatomo } from 'contexts/MatomoContext';
import { checkHasToken, getServerError, navigateByReturnToUrl } from 'utils';
import { withSuccessActionsDialog } from 'utils/enhancers';
import { SignInParams } from 'types';
import { CATEGORY_NAMES, METRIC_EVENT_NAMES } from 'metrics/constants';
import { COLORS } from 'theme/constants';
import {
  BuilderAuthCard,
  BuilderTextValidator,
  BuilderStyledButton,
  Box,
  BuilderFormLink,
  BuilderTitle,
  BuilderSubtitle,
} from 'pages/auth/style';

import { BuilderContactUsFooter } from '../BuilderContactUsFooter';

import { ReactComponent as GoogleIcon } from 'assets/img/google.svg';
import { ReactComponent as SuccessIcon } from 'assets/img/builder-success-icon.svg';

interface BuilderSignInPageProps {
  setSuccessActionMessages: (messages: string[]) => void;
  title?: string;
  message?: string;
}

const initialState: SignInParams = {
  clientName: '',
  email: '',
  password: '',
};

const BuilderSignIn = ({ setSuccessActionMessages, title, message }: BuilderSignInPageProps) => {
  const {
    currentUser: { isSignedIn, attributes },
  } = useAuthState();
  const { trackEvent } = useMatomo();

  const [state, setState] = useState<SignInParams>(initialState);
  const [autoFill, setAutoFill] = useState({
    [authModel.fields.email]: false,
    [authModel.fields.password]: false,
  });
  const [firstStepAuth, setFirstStepAuth] = useState(true);
  const history = useHistory();
  const { login } = useAuthActions();
  const { handleServerError } = useServerErrorActions();
  const { isSubmitting, withHandlingSubmittingState } = useSubmittingState();
  const resetClientGlobalContexts = useResetClientGlobalContexts();
  const { t } = useTranslation();
  const otpRef = useRef(null);

  useEffect(() => {
    resetClientGlobalContexts();
  }, []);

  useEffect(() => {
    if (!isSignedIn) {
      return;
    }
    if (attributes.enabled) {
      if (checkHasToken() && attributes.otpNeedEnable && !attributes.otpRequiredForLogin) {
        history.push(`${ROUTES.tFASettingsNew}${window.location.search}`);
      } else if (attributes.id && checkHasToken()) {
        navigateByReturnToUrl();
        history.push(ROUTES.home);
      }
    } else {
      handleServerError(getServerError(t('common.errors.noAccess')));
    }
  }, [isSignedIn, attributes, checkHasToken()]);

  const onResetGA = () =>
    tfaModel
      .sendResetToEmail(pick(state, ['email', 'clientName']))
      .then(({ success }) => setSuccessActionMessages(success))
      .catch(handleServerError);

  const submitForm = () =>
    authModel
      .signIn(state as SignInParams)
      .then(attributes => {
        trackEvent({ category: CATEGORY_NAMES.PUBTOOL_EVENTS, action: 'click', name: METRIC_EVENT_NAMES.LOGIN });
        if (attributes?.enabled) {
          login({
            currentUser: {
              isLoading: false,
              isSignedIn: true,
              attributes,
            },
          });
        } else if (attributes?.id && !attributes?.enabled) {
          handleServerError(getServerError(t('common.errors.noAccess')));
        } else if (!attributes?.id && firstStepAuth) {
          setFirstStepAuth(false);
        } else {
          handleServerError(getServerError(t('common.errors.noAccess')));
        }
      })
      .catch(handleServerError);

  const handleChange = (item: Record<string, string>) => {
    const key = Object.keys(item)[0];

    setAutoFill({ ...autoFill, [key]: false });
    setState(prevState => ({
      ...prevState,
      ...item,
    }));
  };

  const onSignUp = () => {
    trackEvent({
      category: CATEGORY_NAMES.PUBTOOL_EVENTS,
      action: 'click',
      name: METRIC_EVENT_NAMES.CREATE_NEW_PROFILE,
    });
    history.push(ROUTES.authSignUp);
  };

  const onForgotPassword = () => {
    trackEvent({ category: CATEGORY_NAMES.PUBTOOL_EVENTS, action: 'click', name: METRIC_EVENT_NAMES.FORGOT_PASSWORD });
    history.push(ROUTES.resetPassword);
  };

  const onSignInWithGoogle = () => {
    trackEvent({ category: CATEGORY_NAMES.PUBTOOL_EVENTS, action: 'click', name: METRIC_EVENT_NAMES.SIGN_IN_GOOGLE });
    authModel.redirectToGoogleAuth();
  };

  const disableSignInButton = [authModel.fields.email, authModel.fields.password].some(
    field => state[field as keyof SignInParams] === '' && !autoFill[field],
  );

  if (!firstStepAuth) {
    return (
      <BuilderAuthCard>
        <Helmet title={t('pages.auth.signInPage.tabTitle')} />
        <Grid container direction="column" spacing={6}>
          <Grid item>
            <Typography variant="h2">{t('pages.auth.signInPage.title')}</Typography>
          </Grid>
          <Grid item>
            <ValidatorForm onSubmit={() => withHandlingSubmittingState(submitForm)()} style={{ width: '100%' }}>
              <BuilderTextValidator
                validators={[VALIDATORS_MAP.required]}
                label={t('pages.auth.signInPage.googleCodeInputLabel')}
                value={state.otpAttempt}
                fieldName="otpAttempt"
                handleChange={handleChange}
                autoFocus
                inputRef={otpRef}
              />
              <Grid container justify="space-between">
                <Grid item>
                  <BuilderFormLink onClick={onResetGA} variant="text" text={t('pages.auth.signInPage.resetPassBtn')} />
                </Grid>
                <Grid item>
                  <ButtonComponent color="primary" type="submit" text={t('pages.auth.signInPage.2faSignInBtn')} />
                </Grid>
              </Grid>
            </ValidatorForm>
          </Grid>
        </Grid>
      </BuilderAuthCard>
    );
  }

  return (
    <BuilderAuthCard>
      <Helmet title={t('pages.auth.signInPage.tabTitle')} />
      <div style={{ flexGrow: 1 }} />
      <Grid container direction="column" alignItems="center">
        <Grid item sm xl={7} style={{ flex: 1, padding: 0 }}>
          {title && message ? (
            <>
              <Box mb={4}>
                <SuccessIcon />
              </Box>
              <BuilderTitle>{title}</BuilderTitle>
              <Box mt={4} justifyContent="center">
                <BuilderSubtitle>{message}</BuilderSubtitle>
              </Box>
            </>
          ) : (
            <>
              <BuilderTitle>Welcome back!</BuilderTitle>
              <Box mt={4} justifyContent="center">
                <BuilderSubtitle>Happy to have you here!</BuilderSubtitle>
              </Box>
            </>
          )}
        </Grid>
        <Grid item sm xl={7} style={{ flex: 1 }}>
          <ValidatorForm onSubmit={() => withHandlingSubmittingState(submitForm)()} style={{ width: '100%' }}>
            <Box mt={9} mb={4}>
              <BuilderTextValidator
                validators={[VALIDATORS_MAP.spaceName]}
                label="World Name"
                value={state.clientName}
                fieldName={authModel.fields.space}
                handleChange={handleChange}
                autoComplete="on"
                autoFocus
                inputProps={{
                  autoCapitalize: 'none',
                }}
                disableMinHeight
              />
            </Box>
            <Box mb={4}>
              <BuilderTextValidator
                validators={[VALIDATORS_MAP.required, VALIDATORS_MAP.isEmail]}
                label={t('common.forms.labels.email')}
                value={state.email}
                fieldName={authModel.fields.email}
                handleChange={handleChange}
                autoComplete="email"
                type="email"
                onAutoFill={() => setAutoFill(prevState => ({ ...prevState, [authModel.fields.email]: true }))}
                disableMinHeight
              />
            </Box>
            <BuilderTextValidator
              validators={[VALIDATORS_MAP.required]}
              label={t('common.forms.labels.password')}
              value={state.password}
              fieldName={authModel.fields.password}
              handleChange={handleChange}
              autoComplete="current-password"
              type="password"
              onAutoFill={() => setAutoFill(prevState => ({ ...prevState, [authModel.fields.password]: true }))}
              disableMinHeight
            />
            <Box mt={1}>
              <BuilderFormLink
                text={t('pages.auth.signInPage.forgotPassword')}
                color="secondary"
                variant="text"
                onClick={onForgotPassword}
              />
            </Box>
            <Box mt={5}>
              <BuilderStyledButton
                fullWidth
                color="primary"
                type="submit"
                text="Log In"
                variant="contained"
                loading={isSubmitting}
                disabled={disableSignInButton}
              />
            </Box>
          </ValidatorForm>
        </Grid>
        <Grid item sm xl={7} style={{ flex: 1 }}>
          <Box my={5}>
            <Typography style={{ color: COLORS.white }}>OR</Typography>
          </Box>
        </Grid>
        <Grid item container justify="center">
          <Grid item sm xl={7}>
            <BuilderStyledButton
              fullWidth
              color="primary"
              text="Log In with Google"
              variant="outlined"
              Icon={GoogleIcon}
              onClick={onSignInWithGoogle}
            />
          </Grid>
        </Grid>
        <Grid item>
          {!title && !message && (
            <Box mt={10}>
              <Typography style={{ color: COLORS.builderSubTitleColor }}>Don&#39;t have an account?&nbsp;</Typography>
              <BuilderFormLink text="Sign Up" variant="text" onClick={onSignUp} style={{ fontSize: '14px' }} />
            </Box>
          )}
        </Grid>
      </Grid>
      <div style={{ flexGrow: 2 }} />
      <BuilderContactUsFooter />
      <div style={{ flexGrow: 0.3 }} />
    </BuilderAuthCard>
  );
};

export default withSuccessActionsDialog(BuilderSignIn);
